import { Injectable } from "@angular/core";
import { StatusCodes } from "@obg/ngx.global";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { CoreHttpClient } from "../../http-client/http-client.service";

import { DepositLimitConfigService } from "./deposit-limit.config";
import {
	DepositLimit,
	DepositLimitParams,
	DepositLimitPeriodType,
	DepositLimits,
	DepositLimitType,
	DepositLimitUnconfirmedParams,
	DepositLimitV2,
} from "./deposit-limit.model";

/**
 * @deprecated [fabric-deprecated] - Use DepositLimitService from app-responsible-gaming instead
 */
@Injectable({
	providedIn: "root",
})
export class DepositLimitService {
	private depositLimitTypeUrlParam = ":depositLimitType";

	constructor(
		private httpClient: CoreHttpClient,
		private config: DepositLimitConfigService
	) {}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetAllDepositLimits instead
	 */
	getAll(): Observable<DepositLimits> {
		if (this.config.getIsV2EndpointEnabled()) {
			return this.httpClient
				.get<DepositLimitV2[]>({
					url: this.config.getDepositLimitEndpoint(),
					suppressErrorLogging: [StatusCodes.Unauthorized],
				})
				.pipe(
					map((response: DepositLimitV2[]): DepositLimits => {
						return this.mapDepositLimitV2ToDepositLimits(response);
					})
				);
		}

		return this.httpClient.get<DepositLimits>({
			url: this.config.getDepositLimitEndpoint(),
			suppressErrorLogging: [StatusCodes.Unauthorized],
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateDepositLimit instead
	 */
	update(params: DepositLimitParams): Observable<DepositLimit> {
		if (this.config.getIsV2EndpointEnabled()) {
			return this.httpClient
				.put<DepositLimitV2[]>({
					url: `${this.config.getDepositLimitEndpoint()}/ResponsibleGaming`,
					data: {
						limit: params.limit,
						depositLimitPeriod: params.depositLimitPeriod,
						depositLimitCalculationType: params.depositLimitType,
					},
				})
				.pipe(
					map(
						(response: DepositLimitV2[]): DepositLimit =>
							this.mapDepositLimitV2ToDepositLimit(response, params.depositLimitType)
					)
				);
		}

		return this.httpClient.put<DepositLimit>({
			url: `${this.config.getDepositLimitEndpoint()}/${this.depositLimitTypeUrlParam}`,
			data: params,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.RemoveDepositLimit instead
	 */
	remove(depositLimitType: DepositLimitType): Observable<DepositLimit> {
		if (this.config.getIsV2EndpointEnabled()) {
			return this.httpClient
				.delete<DepositLimitV2[]>({
					url: `${this.config.getDepositLimitEndpoint()}/ResponsibleGaming`,
					data: { depositLimitType },
				})
				.pipe(
					map(
						(response: DepositLimitV2[]): DepositLimit =>
							this.mapDepositLimitV2ToDepositLimit(response, depositLimitType)
					)
				);
		}

		return this.httpClient.delete<DepositLimit>({
			url: `${this.config.getDepositLimitEndpoint()}/${this.depositLimitTypeUrlParam}`,
			data: { depositLimitType },
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetUnconfirmedDepositLimits instead
	 */
	getUnconfirmed(): Observable<DepositLimits> {
		return this.httpClient.get<DepositLimits>({ url: this.config.getUnconfirmedDepositLimitEndpoint() });
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.AcceptUnconfirmedDepositLimit instead
	 */
	acceptUnconfirmed(params: DepositLimitUnconfirmedParams): Observable<void> {
		return this.httpClient.put<void>({
			url: this.config.getUnconfirmedDepositLimitEndpoint(),
			data: params,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.DeclineUnconfirmedDepositLimit instead
	 */
	declineUnconfirmed(params: DepositLimitUnconfirmedParams): Observable<void> {
		return this.httpClient.delete<void>({
			url: this.config.getUnconfirmedDepositLimitEndpoint(),
			data: params,
		});
	}

	private mapDepositLimitV2ToDepositLimits(newData: DepositLimitV2[]): DepositLimits {
		const result: DepositLimits = {
			responsibleGaming: undefined,
			pureDepositLimit: undefined,
			netDepositLimit: undefined,
			temporaryAccountLifetimeLimit: undefined,
		};

		const toOld = (d: DepositLimitV2): DepositLimit => {
			const value = d.values[0];
			return {
				amount: value.amount,
				period: value.period as DepositLimitPeriodType,
				startDate: d.startDate,
				endDate: d.endDate,
				createdDate: d.createDate,
				depositLimitType: d.calculationType,
				depositLimitId: d.depositLimitId,
			};
		};

		const mapType = (type: DepositLimitType, targetKey: keyof DepositLimits) => {
			const active = newData.find((d) => d.calculationType === type && d.status === "Active");
			const queued = newData.find((d) => d.calculationType === type && d.status === "Queued");

			if (active) {
				const oldLimit = toOld(active);
				if (queued) {
					oldLimit.queuedDepositLimit = toOld(queued);
				}
				result[targetKey] = oldLimit;
			}
		};

		mapType(DepositLimitType.NetDepositLimit, "responsibleGaming");
		mapType(DepositLimitType.PureDepositLimit, "pureDepositLimit");
		mapType(DepositLimitType.NetDepositLimit, "netDepositLimit");
		mapType(DepositLimitType.TemporaryAccountLifetimeLimit, "temporaryAccountLifetimeLimit");

		return result;
	}

	private mapDepositLimitV2ToDepositLimit(v2List: DepositLimitV2[], type: DepositLimitType): DepositLimit {
		const matchingLimits = v2List.filter((v2) => v2.calculationType === type);

		if (matchingLimits.length === 0) {
			throw new Error(`No deposit limits found for type ${type}`);
		}

		const activeLimit = matchingLimits
			.filter((l) => l.status === "Active")
			.sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())[0];

		const queuedLimit = matchingLimits
			.filter((l) => l.status === "Queued")
			.sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())[0];

		if (!activeLimit) {
			throw new Error(`No active deposit limit found for type ${type}`);
		}

		const activeValue = activeLimit.values[0];
		const mappedActive: DepositLimit = {
			amount: activeValue.amount,
			period: activeValue.period as DepositLimitPeriodType,
			startDate: activeLimit.startDate,
			endDate: activeLimit.endDate,
			createdDate: activeLimit.createDate,
			depositLimitType: type,
			depositLimitId: activeLimit.depositLimitId,
		};

		if (queuedLimit) {
			const queuedValue = queuedLimit.values[0];
			mappedActive.queuedDepositLimit = {
				amount: queuedValue.amount,
				period: queuedValue.period as DepositLimitPeriodType,
				startDate: queuedLimit.startDate,
				createdDate: queuedLimit.createDate,
				depositLimitType: type,
				depositLimitId: queuedLimit.depositLimitId,
			};
		}

		return mappedActive;
	}
}
